// GatewayDayModal.js
// Modal showing 5-minute interval timeline for a specific gateway on a specific day

import React, { useEffect, useState } from 'react';
import './GatewayDayModal.css';

export default function GatewayDayModal({ company, store, gateway, gatewayName, day, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [intervals, setIntervals] = useState([]);
  const [stats, setStats] = useState(null);
  const [gwInfo, setGwInfo] = useState(null);
  const [hoveredSlot, setHoveredSlot] = useState(null);

  useEffect(() => {
    if (!company || !store || !gateway || !day) return;

    async function fetchDayData() {
      try {
        setLoading(true);
        setError(null);

        // Request the calendar day directly in UTC (data is stored UTC midnight to midnight)
        // For 2025-12-15, we want: "2025-12-15 00:00:00" to "2025-12-16 00:00:00" (UTC)
        const [year, month, dayNum] = day.split('-').map(Number);

        // Calculate next day for 24-hour range
        const currentDate = new Date(Date.UTC(year, month - 1, dayNum));
        const nextDate = new Date(currentDate);
        nextDate.setUTCDate(nextDate.getUTCDate() + 1);
        const nextDay = nextDate.toISOString().slice(0, 10);

        const dayStart = `${day} 00:00:00`;
        const dayEnd = `${nextDay} 00:00:00`;

        const response = await fetch(
          `/api/gateway-uptime/store-gateways-timeline?company=${encodeURIComponent(company)}&store=${encodeURIComponent(store)}&startTime=${encodeURIComponent(dayStart)}&endTime=${encodeURIComponent(dayEnd)}`,
          { credentials: 'include' }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch timeline: ${response.statusText}`);
        }

        const data = await response.json();

        // Find our specific gateway
        const gatewayData = data.gateways.find(gw => gw.gwId === gateway);

        if (!gatewayData || !gatewayData.timeline) {
          throw new Error('No timeline data found for this gateway');
        }

        const totalIntervals = gatewayData.timeline.length;
        const onlineIntervals = gatewayData.timeline.filter(i => i.status === 'online').length;
        const offlineIntervals = totalIntervals - onlineIntervals;

        // Calculate hours (each interval is 5 minutes)
        const onlineHours = (onlineIntervals * 5) / 60;
        const offlineHours = (offlineIntervals * 5) / 60;
        const coverageHours = (totalIntervals * 5) / 60;
        const coveragePercent = (coverageHours / 24) * 100;

        // Sort intervals chronologically by timeInterval
        const sortedIntervals = [...gatewayData.timeline].sort((a, b) => {
          return a.timeInterval.localeCompare(b.timeInterval);
        });

        // Find first and last data points
        const firstInterval = sortedIntervals.length > 0 ? sortedIntervals[0].timeInterval : null;
        const lastInterval = sortedIntervals.length > 0 ? sortedIntervals[sortedIntervals.length - 1].timeInterval : null;

        setIntervals(sortedIntervals);
        setGwInfo({
          ip: gatewayData.ip || null,
          serverInsertTime: gatewayData.serverInsertTime || null
        });
        setStats({
          totalIntervals,
          onlineIntervals,
          offlineIntervals,
          uptimePercent: gatewayData.uptimePercent,
          onlineHours: onlineHours.toFixed(1),
          offlineHours: offlineHours.toFixed(1),
          coverageHours: coverageHours.toFixed(1),
          coveragePercent: coveragePercent.toFixed(1),
          firstInterval,
          lastInterval
        });

      } catch (err) {
        console.error('Gateway day fetch error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    fetchDayData();
  }, [company, store, gateway, day]);

  const formatTime = (timeStr) => {
    // SaaS API returns timestamps in UTC format: "2026-01-08 14:35:00"
    // Convert to local browser timezone for display
    const date = new Date(timeStr.replace(' ', 'T') + 'Z'); // Add 'Z' to indicate UTC
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const formatDayHeader = (dayStr) => {
    // Parse date string without timezone conversion
    // "2026-01-14" should display as "January 14, 2026", not "January 13, 2026"
    const [year, month, day] = dayStr.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };

  // Remove company suffix from gateway ID (e.g., "D02544FFFE1F77C6BPS" -> "D02544FFFE1F77C6")
  const cleanGatewayId = gateway.replace(/BPS$|WFC$|WLAB$/, '');

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content gateway-day-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="header-title">Gateway Timeline</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="gateway-info-section">
          <div className="gateway-mac">{cleanGatewayId}</div>
          <div className="gateway-date">{formatDayHeader(day)}</div>
          <div className="gateway-first-connected">
            {gwInfo?.serverInsertTime ? (
              <>Gateway first connected on: {new Date(gwInfo.serverInsertTime).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}</>
            ) : (
              <>Registered but never connected</>
            )}
          </div>
        </div>

        <div className="modal-body">
          {loading && (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading timeline data...</p>
            </div>
          )}

          {error && (
            <div className="error-state">
              <p style={{ color: '#e74c3c' }}>Error: {error}</p>
            </div>
          )}

          {!loading && !error && stats && (
            <>
              {/* Info banner if partial day coverage */}
              {stats.coveragePercent < 100 && stats.firstInterval && (
                <div className="info-banner">
                  <span className="info-icon">ℹ️</span>
                  <span className="info-text">
                    Data collection started at <strong>{formatTime(stats.firstInterval)}</strong>
                    {stats.lastInterval && ` and ended at ${formatTime(stats.lastInterval)}`}
                    . This gateway may have been added mid-day or data collection was interrupted.
                  </span>
                </div>
              )}

              <div className="stats-bar">
                <div className="stat-item">
                  <span className="stat-label">Uptime</span>
                  <span className={`stat-value ${stats.uptimePercent >= 95 ? 'good' : stats.uptimePercent >= 80 ? 'warning' : 'bad'}`}>
                    {stats.uptimePercent.toFixed(1)}%
                  </span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Data Coverage</span>
                  <span className="stat-value">{stats.coverageHours} hrs / 24 hrs ({stats.coveragePercent}%)</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Online Time</span>
                  <span className="stat-value good">{stats.onlineHours} hrs</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Offline Time</span>
                  <span className="stat-value bad">{stats.offlineHours} hrs</span>
                </div>
              </div>

              <div className="timeline-container">
                <h3>5-Minute Intervals Timeline</h3>
                <div className="timeline-rows-container">
                  {(() => {
                    // Create a lookup map for faster access: "HH:MM" (local time) -> interval object
                    const intervalMap = new Map();
                    intervals.forEach(interval => {
                      // interval.timeInterval is in UTC format: "YYYY-MM-DD HH:MM:SS"
                      // Convert to local time for proper mapping
                      const utcDateStr = interval.timeInterval.replace(' ', 'T') + 'Z';
                      const localDate = new Date(utcDateStr);
                      const localHours = String(localDate.getHours()).padStart(2, '0');
                      const localMinutes = String(localDate.getMinutes()).padStart(2, '0');
                      const localTime = `${localHours}:${localMinutes}`;

                      intervalMap.set(localTime, interval);
                    });

                    const renderRow = (startHour, endHour, label) => {
                      const slots = Array.from({ length: 144 }, (_, idx) => {
                        // Calculate time for this slot within the row
                        const minutesFromRowStart = idx * 5;
                        const hours = startHour + Math.floor(minutesFromRowStart / 60);
                        const minutes = minutesFromRowStart % 60;
                        const expectedTime = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

                        // Look up interval data by local time
                        const matchingInterval = intervalMap.get(expectedTime);

                        const status = matchingInterval ? matchingInterval.status : 'missing';
                        const tooltip = matchingInterval
                          ? `${formatTime(matchingInterval.timeInterval)} - ${matchingInterval.status.toUpperCase()}`
                          : `${expectedTime} - NO DATA`;

                        return (
                          <div
                            key={idx}
                            className={`interval-bar ${status}`}
                            title={tooltip}
                          >
                            <div className="bar-fill" />
                          </div>
                        );
                      });

                      return (
                        <div className="timeline-row">
                          <div className="timeline-row-label">{label}</div>
                          <div className="timeline-bar-chart">
                            {slots}
                          </div>
                          <div className="timeline-labels">
                            <span style={{ left: '0%' }}>{startHour === 0 ? '12:00 AM' : '12:00 PM'}</span>
                            <span style={{ left: '25%' }}>{startHour === 0 ? '3:00 AM' : '3:00 PM'}</span>
                            <span style={{ left: '50%' }}>{startHour === 0 ? '6:00 AM' : '6:00 PM'}</span>
                            <span style={{ left: '75%' }}>{startHour === 0 ? '9:00 AM' : '9:00 PM'}</span>
                            <span style={{ left: '100%' }}>{startHour === 0 ? '11:59 AM' : '11:59 PM'}</span>
                          </div>
                        </div>
                      );
                    };

                    return (
                      <>
                        {renderRow(0, 12, 'Morning (12:00 AM - 11:59 AM)')}
                        {renderRow(12, 24, 'Afternoon/Evening (12:00 PM - 11:59 PM)')}
                      </>
                    );
                  })()}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-close" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
